import type { GetStaticPaths, GetStaticProps } from "next";
import {
  ArticleWrapper,
  DynamicLink,
  SectionPage,
  BlogStickyShare,
  RelatedArticles,
  getTaxonomyTags,
  BlogAuthorByNpi,
  BlogCTA,
  BlogNavigation,
  TaxonomyTags,
  Icon,
  EventMicrodata,
  ArticleHeroMedia,
  getArticleHeroImageUrl,
  resolveArticleHeroMedia,
  SP_DICTIONARY_KEYS,
  resolveAuthorByNpi,
  buildBreadcrumbsFromPage,
  normalizeSiteId,
  getAllContentHubArticlesCached,
  getContentHubArticleFetchOptions,
  getContentHubArticleSingleFetchOptions,
  prepareContentHubArticles,
  resolveContentHubArticleForLocale,
  getTaxonomyForBuild,
  fetchContentHubArticleBySlug,
  contentHubSlugsEqual,
  ensureCurrentArticleInList,
} from "@orlandohealth/build-kit";
import { BuilderContent, BuilderComponent, builder } from "@builder.io/react";
import React from "react";

// Create a ArticlePage class that extends SectionPage
class ArticlePage extends SectionPage {
  static async getStaticProps(
    urlPath: string,
    slug: string,
    locale?: string,
    defaultLocale?: string,
    model: string = "article"
  ) {
    return super.getStaticProps(urlPath, slug, locale, defaultLocale, model);
  }

  protected static renderContent(props: any, model: string, router: any) {
    const viewAllHeading = props.siteProperties?.getDictionaryValue?.(
      SP_DICTIONARY_KEYS.DICTIONARY_BLOG_VIEW_ALL_HEADING
    ) || "View All Articles";
    const currentSlug = router.query.slug?.[0];
    const currentLocale = router?.locale || router?.defaultLocale || "en";

    // Key by article so content updates when navigating to a different article (e.g. from related articles)
    const articleKey = props.content?.id ?? currentSlug ?? router.asPath ?? "article";
    return (
      <>
        <BuilderContent
          key={articleKey}
          content={props.content || null}
          options={{ includeRefs: true, noTraverse: true, locale: currentLocale }}
          model={model}
        >
          {(content, loading, fullContent) => {
            if (loading) {
              return <div>Loading...</div>;
            }

            const heroMedia = resolveArticleHeroMedia(
              content,
              fullContent,
              props.content
            );

            const getShareUrl = () => {
              if (typeof window !== "undefined") {
                return window.location.href;
              }
              return `${process.env.NEXT_PUBLIC_SITE_URL || ""}${router.asPath}`;
            };

            // Event microdata logic
            const isEvent = content?.type === "event";
            const eventFields = content?.eventData;
            const eventData = isEvent && eventFields
              ? {
                name: content.title,
                startDate: eventFields.startDate,
                endDate: eventFields.endDate,
                description: content.description,
                image: content.media?.additionalInfo?.selectedFile?.url ? [content.media?.additionalInfo?.selectedFile?.url] : undefined,
                location: {
                  name: content.title,
                  streetAddress: [eventFields.address1, eventFields.address2].filter(Boolean).join(", "),
                  addressLocality: eventFields.city,
                  addressRegion: eventFields.state,
                  postalCode: eventFields.zipCode,
                },
              }
              : null;
            return (
              <>
                {isEvent && eventData && (
                  <EventMicrodata event={eventData} />
                )}
                <div id="article-content" className="mx-auto max-w-[800px] md:mt-8 mt-4">
                  <div className="container">
                    <div className="font-secondary font-medium text-sm pb-5">
                      <DynamicLink
                        link={{ type: "url", href: "/content-hub" }}
                        className="text-neutral-20 hover:underline underline-offset-4 inline-flex items-center"
                      >
                        <Icon icon="chevron-left" size="sm" className="mr-1" />{" "}
                        {viewAllHeading}
                      </DynamicLink>
                    </div>
                    <div className="relative -mx-5 sm:mx-0">
                      <div className="relative w-full aspect-[16/9]">
                        <ArticleHeroMedia
                          media={heroMedia}
                          altText={content?.title || ""}
                          priority
                        />
                      </div>
                    </div>
                    <article>
                      <TaxonomyTags
                        taxonomies={content?.taxonomies}
                        taxonomyTree={props.taxonomies}
                      />
                      {content?.title && (
                        <h1 className="pt-4 text-lg md:text-3xl font-bold text-neutral-20">
                          {content?.title}
                        </h1>
                      )}
                      {content?.subtitle && (
                        <h4>{content?.subtitle}</h4>
                      )}
                      {content?.publishDate && (
                        <div className="mt-4 font-secondary text-neutral-40">
                          {new Date(content.publishDate).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </div>
                      )}
                      {(content?.author || fullContent?.data?.author) && (
                        <div className="mt-6 mb-8">
                          <BlogAuthorByNpi
                            authorData={content?.author?.value?.data ?? fullContent?.data?.author?.value?.data}
                            authorRefId={content?.author?.id ?? fullContent?.data?.author?.id}
                            resolvedProvider={props.resolvedAuthorProvider ?? undefined}
                          />
                        </div>
                      )}

                      <BuilderComponent
                        key="blog-content"
                        model="article"
                        content={fullContent}
                        options={{ includeRefs: true }}
                        locale={currentLocale}
                      />
                    </article>
                  </div>
                </div>

                {Array.isArray(props.ctas) && props.ctas.length > 0 && (
                  <BlogCTA
                    image={props.ctas[0]?.data?.image?.additionalInfo?.selectedFile?.url}
                    altText={props.ctas[0]?.data?.altText?.Default || ""}
                    headline={props.ctas[0]?.data?.headline?.Default || ""}
                    body={props.ctas[0]?.data?.body?.Default || ""}
                    ctaText={props.ctas[0]?.data?.ctaText?.Default || ""}
                    href={props.ctas[0]?.data?.ctaLink?.Default || ""}
                    id={props.ctas[0]?.id}
                  />
                )}

                <BlogNavigation
                  key={currentSlug}
                  previousArticle={props.previousArticle}
                  nextArticle={props.nextArticle}
                />
                <BlogStickyShare
                  url={getShareUrl()}
                  title={content?.title}
                  summary={content?.subtitle}
                  media={getArticleHeroImageUrl(heroMedia)}
                />

                {Array.isArray(props.relatedArticles) &&
                  props.relatedArticles.length > 0 && (
                    <RelatedArticles
                      articles={props.relatedArticles}
                      heading="Related Articles"
                      taxonomyTree={props.taxonomies}
                    />
                  )}
              </>
            );
          }}
        </BuilderContent>
      </>
    );
  }
}

// Use SectionPage
const Page = ArticlePage.createPage(ArticleWrapper);

// Export the page component
export default Page;

// Add a helper function to ensure all objects are serializable
const ensureSerializable = (obj: any): any => {
  if (obj === undefined) {
    return null;
  }

  if (obj === null) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(ensureSerializable);
  }

  if (typeof obj === 'object') {
    const result: Record<string, any> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = ensureSerializable(obj[key]);
      }
    }
    return result;
  }

  return obj;
};

// Add a helper function to extract the title correctly regardless of format
const getArticleTitle = (title: any): string => {
  if (!title) return "";

  // If it's a string, return it directly
  if (typeof title === 'string') return title;

  // If it's an object with a Default property (localized value)
  if (typeof title === 'object' && title !== null) {
    if (title['Default']) return title['Default'];

    // If it has a @type property indicating it's a localized value
    if (title['@type'] === '@builder.io/core:LocalizedValue') {
      return title['Default'] || "";
    }
  }

  // Fallback: try to convert to string
  return String(title);
};

// Implement getStaticProps
export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  defaultLocale,
}) => {
  try {
    const urlPath = `/content-hub/${(params?.slug as string[])?.join("/") || ""}`;
    const currentLocale = locale || defaultLocale;
    const slug = (params?.slug as string[])?.[0];
    if (!slug?.trim()) {
      return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
    }
    const CURRENT_SITE = normalizeSiteId(process.env.NEXT_PUBLIC_REGIONAL_CARE_SITE || "orlando");

    // Store the current API key
    const originalApiKey = builder.apiKey;

    try {
      // 1. First, use the default API key for local site components
      builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";
      // Get base props from SectionPage using local builder.
      // Breadcrumbs are recomputed below from the resolved article content
      // (corp-space fetch), so skip the redundant lookup inside getBaseStaticProps.
      const baseProps = await SectionPage.getStaticProps(
        urlPath,
        slug,
        locale,
        defaultLocale,
        "article",
        { skipBreadcrumbs: true }
      );

      // 2. Switch to the corp API key for content hub data and articles
      builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || ""; // Corp API key

      // Get the specific article content directly from corp space
      let articleContent = null;
      let allArticles: any[] = [];
      let resolvedAuthorProvider: any = null;
      try {
        const { options: articleOptions, locale: articleLocale } = getContentHubArticleSingleFetchOptions(currentLocale);
        articleContent = resolveContentHubArticleForLocale(
          await fetchContentHubArticleBySlug(slug, {
            apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
            options: articleOptions,
            locale: articleLocale,
          }),
          currentLocale
        );

        if (!articleContent) {
          throw new Error(`Article with slug "${slug}" not found in corp space`);
        }

        // Verify regional care site matching
        const hasRegionalCareSites = Array.isArray(articleContent.data?.regionalCareSites);
        const isTaggedForSite = hasRegionalCareSites && articleContent.data?.regionalCareSites?.some((site: any) => {
          let siteName = '';
          if (typeof site === 'string') {
            siteName = site;
          } else if (site && typeof site === 'object') {
            if (site.Name) siteName = site.Name;
            else if (site.name) siteName = site.name;
            else if (site.value) siteName = site.value;
            else if (site.id) siteName = site.id;
          }
          return normalizeSiteId(siteName) === CURRENT_SITE;
        });

        if (!isTaggedForSite) {
          console.warn(`[Content Hub] Article "${slug}" exists but is not tagged for "${CURRENT_SITE}" site. Regional care sites:`, articleContent.data?.regionalCareSites);
          return {
            notFound: true,
            revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
          };
        }

        const currentUrl = `/content-hub/${(params?.slug as string[])?.join("/") || ""}`;
        const targetingScope = articleContent?.data?.metadata?.targetingScope;

        const [
          globalAlerts,
          targetAlerts,
          globalCTAs,
          targetCTAs,
          allArticlesResult,
          resolvedAuthorProviderResult,
        ] = await Promise.all([
          builder.getAll("alert", { userAttributes: { scope: "global" } }).catch(() => []),
          builder.getAll("alert", { userAttributes: { scope: targetingScope } }).catch(() => []),
          builder.getAll("cta", { userAttributes: { scope: "global", urlPath: currentUrl } }).catch(() => []),
          builder.getAll("cta", { userAttributes: { scope: targetingScope, urlPath: currentUrl } }).catch(() => []),
          getAllContentHubArticlesCached(currentLocale || "en").catch(() => []),
          resolveAuthorByNpi(articleContent).catch(() => null),
        ]);

        allArticles = prepareContentHubArticles(allArticlesResult, currentLocale);
        resolvedAuthorProvider = resolvedAuthorProviderResult;

        builder.apiKey = originalApiKey;

        const alerts = [...globalAlerts, ...targetAlerts];
        const ctas = [...globalCTAs, ...targetCTAs];
        baseProps.props.alerts = alerts;
        baseProps.props.ctas = ctas;
      } catch (articleError) {
        console.error(`Error fetching article content: ${articleError instanceof Error ? articleError.message : String(articleError)}`);
      }

      if (!articleContent) {
        builder.apiKey = originalApiKey;
        return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
      }

      // Filter articles by regionalCareSites. Site tagging for *this* article was
      // already verified on the cache-busted single fetch above — if the bulk
      // list is stale, include the current article instead of 404ing.
      const filteredArticles = ensureCurrentArticleInList(
        allArticles.filter((article: any) => {
        if (!article.data?.regionalCareSites) {
          return false;
        }

        return Array.isArray(article.data.regionalCareSites) &&
          article.data.regionalCareSites.some((site: any) => {
            let siteName = '';
            if (typeof site === 'string') {
              siteName = site;
            } else if (site && typeof site === 'object') {
              if (site.Name) siteName = site.Name;
              else if (site.name) siteName = site.name;
              else if (site.value) siteName = site.value;
              else if (site.id) siteName = site.id;
            }
            return normalizeSiteId(siteName) === CURRENT_SITE;
          });
        }),
        articleContent,
        slug
      );

      // Sort articles by date
      const sortedArticles = filteredArticles
        .filter((article: any) => article.data?.slug && article.data?.title)
        .sort((a: any, b: any) => {
          const dateA = new Date(a.data?.publishDate || 0).getTime();
          const dateB = new Date(b.data?.publishDate || 0).getTime();
          return dateB - dateA;
        });

      // Find current article index
      const currentIndex = sortedArticles.findIndex(
        (article: any) => contentHubSlugsEqual(article.data?.slug, slug)
      );

      // Get previous and next articles
      let previousArticle =
        currentIndex !== -1 && currentIndex < sortedArticles.length - 1
          ? {
            slug: sortedArticles[currentIndex + 1].data?.slug || "",
            title: getArticleTitle(sortedArticles[currentIndex + 1].data?.title),
          }
          : null;

      let nextArticle =
        currentIndex > 0
          ? {
            slug: sortedArticles[currentIndex - 1].data?.slug || "",
            title: getArticleTitle(sortedArticles[currentIndex - 1].data?.title),
          }
          : null;

      // Get related articles based on taxonomies
      const currentArticle = sortedArticles[currentIndex];
      const relatedArticles = sortedArticles
        .filter((article: any, index: number) => {
          // Skip current article
          if (index === currentIndex) return false;

          // Extract taxonomies from both articles
          const articleTaxonomies = getTaxonomyTags(article.data?.taxonomies);
          const currentArticleTaxonomies = getTaxonomyTags(currentArticle?.data?.taxonomies);

          // If either array is empty, don't consider a match
          if (articleTaxonomies.length === 0 || currentArticleTaxonomies.length === 0) {
            return false;
          }

          // Check if there are matching tags
          return articleTaxonomies.some((tag: string) =>
            currentArticleTaxonomies.includes(tag)
          );
        })
        .slice(0, 3)
        .map((article: any) => {
          // Determine the media type
          const mediaType = typeof article.data?.media === 'string' ? 'url' : 'asset';
          return {
            title: getArticleTitle(article.data?.title),
            slug: article.data?.slug || "",
            media: article.data?.media || null,
            mediaType,
            authorId: article.data?.author?.id ?? null,
            author: article.data?.author?.value?.data
              ? {
                name: [
                  article.data.author.value.data.firstName || "",
                  article.data.author.value.data.middleInitial || "",
                  article.data.author.value.data.lastName || "",
                ]
                  .filter(Boolean)
                  .join(" "),
                href: "",
              }
              : null,
            taxonomies: article.data?.taxonomies,
          };
        });

      // Also check for navigation links if none found
      if (!previousArticle && !nextArticle && filteredArticles.length > 1) {
        // Find the current index in the filtered articles
        const currentFilteredIndex = filteredArticles.findIndex(
          (article: any) => contentHubSlugsEqual(article.data?.slug, slug)
        );

        // If found, get previous and next
        if (currentFilteredIndex !== -1) {
          if (currentFilteredIndex < filteredArticles.length - 1) {
            previousArticle = {
              slug: filteredArticles[currentFilteredIndex + 1].data?.slug || "",
              title: getArticleTitle(filteredArticles[currentFilteredIndex + 1].data?.title),
            };
          }

          if (currentFilteredIndex > 0) {
            nextArticle = {
              slug: filteredArticles[currentFilteredIndex - 1].data?.slug || "",
              title: getArticleTitle(filteredArticles[currentFilteredIndex - 1].data?.title),
            };
          }
        }
      }
      // Use getTaxonomyForBuild: passes locale at the top level so Builder.io's content
      // targeting matches the locale-specific hub home page entry and its taxonomy model.
      const { taxonomies: mergedTaxonomies } = await getTaxonomyForBuild(
        process.env.NEXT_PUBLIC_BUILDER_API_KEY || "",
        process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
        currentLocale || "en_US"
      );

      if (articleContent) {
        baseProps.props.breadcrumbs = await buildBreadcrumbsFromPage(
          urlPath,
          articleContent,
          baseProps.props.builderLocale ?? currentLocale
        );
      }

      return {
        props: {
          ...baseProps.props,
          content: articleContent || null,
          taxonomies: mergedTaxonomies || null,
          previousArticle: previousArticle || null,
          nextArticle: nextArticle || null,
          relatedArticles: relatedArticles || [],
          resolvedAuthorProvider,
        },
        revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
      };
    } catch (innerError) {
      console.error(`Error in getStaticProps inner try/catch: ${innerError instanceof Error ? innerError.message : String(innerError)}`);
      // Ensure we restore the API key even if there's an error
      builder.apiKey = originalApiKey;

      // If the error indicates the article is not tagged for this site, return notFound
      if (innerError instanceof Error && innerError.message.includes('not tagged for')) {
        return {
          notFound: true,
          revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
        };
      }

      // Return minimal props to avoid complete page failure for other errors
      return {
        props: ensureSerializable({
          content: null,
          taxonomies: [],
          previousArticle: null,
          nextArticle: null,
          relatedArticles: [],
          error: `Failed to load article data: ${innerError instanceof Error ? innerError.message : String(innerError)}`
        }),
        revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
      };
    }
  } catch (outerError) {
    console.error(`Critical error in getStaticProps: ${outerError instanceof Error ? outerError.message : String(outerError)}`);

    // If the error indicates the article is not tagged for this site, return notFound
    if (outerError instanceof Error && outerError.message.includes('not tagged for')) {
      return {
        notFound: true,
        revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
      };
    }

    // Return minimal props to avoid complete page failure for other errors
    return {
      props: ensureSerializable({
        content: null,
        taxonomies: [],
        previousArticle: null,
        nextArticle: null,
        relatedArticles: [],
        error: `Critical error loading page: ${outerError instanceof Error ? outerError.message : String(outerError)}`
      }),
      revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
    };
  }
};

export const getStaticPaths: GetStaticPaths = async ({
  locales = ["en_US"],
}) => {
  if (process.env.SKIP_SSG === "true") { return { paths: [], fallback: "blocking" }; }
  const paths = [];
  const CURRENT_SITE = normalizeSiteId(process.env.NEXT_PUBLIC_REGIONAL_CARE_SITE || "orlando");

  const originalApiKey = builder.apiKey;
  builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";

  try {
    for (const locale of locales) {
      // Only pre-generate the 20 most recent articles — the rest render on first request via fallback
      const articles = prepareContentHubArticles(
        (await builder.getAll("article", {
          ...getContentHubArticleFetchOptions(locale, {
            fields: "data.slug,data.regionalCareSites,data.publishDate,data.title",
            limit: 20,
            sort: { "data.publishDate": -1 },
            options: { noTargeting: true },
          }),
        })) as any[],
        locale
      );

      const filteredArticles = articles.filter((article: any) => {
        if (!article.data?.slug) return false;
        if (!article.data?.regionalCareSites) return false;

        return Array.isArray(article.data.regionalCareSites) &&
          article.data.regionalCareSites.some((site: any) => {
            let siteName = '';
            if (typeof site === 'string') {
              siteName = site;
            } else if (site && typeof site === 'object') {
              if (site.Name) siteName = site.Name;
              else if (site.name) siteName = site.name;
              else if (site.value) siteName = site.value;
              else if (site.id) siteName = site.id;
            }
            return normalizeSiteId(siteName) === CURRENT_SITE;
          });
      });

      paths.push(
        ...filteredArticles.map((article: any) => ({
          params: { slug: [article.data?.slug] },
          locale,
        }))
      );
    }
  } finally {
    builder.apiKey = originalApiKey;
  }

  return {
    paths,
    fallback: "blocking",
  };
};